let scrollViewContainer: HTMLElement | null = null; // Horizontal scroll target
let svgWrapper: HTMLElement | null = null; // Vertical scroll target

let getScrollViewRect: (() => DOMRect) | null = null;
let getSvgWrapperRect: (() => DOMRect) | null = null;

let onScroll: ((clientX: number, clientY: number) => void) | null = null;

let running = false;
let hasMousePos = false;

const MAX_SCROLL_SPEED = 20;
const EDGE_SIZE = 40; //This defines how "early" the scroll is initiate.

const mousePos = { x: 0, y: 0 };

// Speed ramps up the deeper the cursor is in the edge zone, so a drag that only
// just touches the edge scrolls gently instead of jumping at full speed.
const edgeSpeed = (distanceIntoEdge: number): number =>
  Math.min(1, distanceIntoEdge / EDGE_SIZE) * MAX_SCROLL_SPEED;

function step() {
  if (!getScrollViewRect || !getSvgWrapperRect) return;

  // mousePos is (0,0) until the first mousemove, which would read as "at the top-left edge".
  if (!hasMousePos) {
    if (running) requestAnimationFrame(step);
    return;
  }

  const scrollViewBoundingRect = getScrollViewRect();
  const ganttChartSvgWrapper = getSvgWrapperRect();

  const scrollBefore = {
    left: scrollViewContainer!.scrollLeft,
    top: svgWrapper!.scrollTop,
  };

  // Auto-scroll horizontally
  if (mousePos.x < scrollViewBoundingRect.left + EDGE_SIZE) {
    scrollViewContainer!.scrollLeft -= edgeSpeed(
      scrollViewBoundingRect.left + EDGE_SIZE - mousePos.x,
    );
  } else if (mousePos.x > scrollViewBoundingRect.right - EDGE_SIZE) {
    scrollViewContainer!.scrollLeft += edgeSpeed(
      mousePos.x - (scrollViewBoundingRect.right - EDGE_SIZE),
    );
  }

  // Auto-scroll vertically
  if (mousePos.y < ganttChartSvgWrapper.top + EDGE_SIZE) {
    svgWrapper!.scrollTop -= edgeSpeed(
      ganttChartSvgWrapper.top + EDGE_SIZE - mousePos.y,
    );
  } else if (mousePos.y > ganttChartSvgWrapper.bottom - EDGE_SIZE) {
    svgWrapper!.scrollTop += edgeSpeed(
      mousePos.y - (ganttChartSvgWrapper.bottom - EDGE_SIZE),
    );
  }

  // Scrolling fires no mousemove, so without this the dragged block stays at its
  // old content position and detaches from the cursor.
  if (
    scrollViewContainer!.scrollLeft !== scrollBefore.left ||
    svgWrapper!.scrollTop !== scrollBefore.top
  ) {
    onScroll?.(mousePos.x, mousePos.y);
  }

  if (running) requestAnimationFrame(step);
}

function trackMouse(e: MouseEvent) {
  mousePos.x = e.clientX;
  mousePos.y = e.clientY;
  hasMousePos = true;
}

export function startAutoScroll(
  horizontalEl: HTMLElement,
  verticalEl: HTMLElement,
  getHorizontalRect: () => DOMRect,
  getVerticalRect: () => DOMRect,
  onScrollCallback?: (clientX: number, clientY: number) => void,
) {
  scrollViewContainer = horizontalEl;
  svgWrapper = verticalEl;

  getScrollViewRect = getHorizontalRect;
  getSvgWrapperRect = getVerticalRect;
  onScroll = onScrollCallback ?? null;

  if (!running) {
    running = true;
    requestAnimationFrame(step);
    window.addEventListener("mousemove", trackMouse);
  }
}

export function stopAutoScroll() {
  running = false;
  hasMousePos = false;
  onScroll = null;
  scrollViewContainer = null;
  svgWrapper = null;
  getScrollViewRect = null;
  getSvgWrapperRect = null;
  window.removeEventListener("mousemove", trackMouse);
}
